/**
 * Package list component with table view and actions.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Tag, Tooltip, Modal, Badge, Popover, Progress } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
  EyeOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { BSPPackage } from '@/types';
import { formatDate, formatFileSize } from '@/utils';
import { resolveUploadPercent } from '@/utils/uploadProgress';
import { useAuth } from '@/hooks/useAuth';
import { useBcrStore } from '@/store/bcrStore';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import BcrButton from '@/components/bcr/BcrButton';
import { CopyExternalLinkButton } from '@/components/packages/CopyExternalLinkButton';
import { useT } from '@/i18n';
import type { Lang } from '@/store/languageStore';

const formatSpeed = (bps: number) =>
  bps > 1024 * 1024
    ? `${(bps / 1024 / 1024).toFixed(1)} MB/s`
    : `${(bps / 1024).toFixed(0)} KB/s`;

// 数组是权威值；老数据只有单值时兜底
const formatVehicle = (list?: string[], single?: string, lang: Lang = 'zh') => {
  const values = list?.length ? list : single ? [single] : [];
  return values.length ? values.join(lang === 'en' ? ', ' : '、') : '-';
};

interface PackageListProps {
  packages: BSPPackage[];
  loading: boolean;
  pagination: TablePaginationConfig;
  projectId?: number;
  onEdit?: (pkg: BSPPackage) => void;
  onDelete?: (pkg: BSPPackage) => void;
  onViewLogs?: (pkg: BSPPackage) => void;
  onPageChange: (page: number, pageSize: number) => void;
}

export const PackageList: React.FC<PackageListProps> = ({
  packages,
  loading,
  pagination,
  projectId,
  onEdit,
  onDelete,
  onViewLogs,
  onPageChange,
}) => {
  const { t, lang } = useT();
  const { isAdmin, isInternal } = useAuth();
  const navigate = useNavigate();
  const { progress } = useUploadProgressStore();
  const { bcrStatusMap, fetchBatchBcrStatus } = useBcrStore();

  // Fetch BCR status for all packages on load (internal users only)
  useEffect(() => {
    if (isInternal && packages.length > 0) {
      const ids = packages.map((p) => p.id);
      fetchBatchBcrStatus(ids);
    }
  }, [isInternal, packages, fetchBatchBcrStatus]);


  const confirmDelete = (pkg: BSPPackage) => {
    Modal.confirm({
      title: t('确认删除'),
      content: t('确定要删除 "{name}" 吗？此操作不可恢复。', { name: `${pkg.name} - ${formatVehicle(pkg.vehicle_models, pkg.vehicle_model, lang)}` }),
      okText: t('删除'),
      okType: 'danger',
      cancelText: t('取消'),
      onOk: () => onDelete?.(pkg),
    });
  };

  const columns: ColumnsType<BSPPackage> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: t('底软包名称'),
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: false,
      render: (name: string, record) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <span className="font-medium">{name}</span>
          <CopyExternalLinkButton
            id={record.id}
            projectId={projectId ?? record.project?.id}
          />
        </div>
      ),
    },
    {
      title: t('说明'),
      dataIndex: 'description',
      key: 'description',
      // 说明是唯一没有宽度的列，会吃掉所有剩余空间；给定宽度后长文本才有裁剪的参照。
      // 实际的宽度上限由 .bsp-clamp-3 的 max-width 控制（auto 布局下 width 仅为建议值）。
      width: 260,
      ellipsis: false,
      render: (description: string) => {
        if (!description) return <span className="text-gray-600">-</span>;
        // 超长说明会把整行撑得很高，这里限制最多 3 行，超出部分省略号截断，
        // 完整内容通过悬浮提示查看。
        return (
          <Tooltip title={description} placement="topLeft">
            <div className="bsp-clamp-3 text-gray-600">{description}</div>
          </Tooltip>
        );
      },
    },
    {
      title: t('车型'),
      dataIndex: 'vehicle_model',
      key: 'vehicle_model',
      width: 120,
      ellipsis: false,
      render: (_: string, record: BSPPackage) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <span>{formatVehicle(record.vehicle_models, record.vehicle_model, lang)}</span>
        </div>
      ),
    },
    {
      title: t('子车型'),
      dataIndex: 'vehicle_subtype',
      key: 'vehicle_subtype',
      width: 110,
      ellipsis: true,
      render: (_: string, record: BSPPackage) =>
        formatVehicle(record.vehicle_subtypes, record.vehicle_subtype, lang),
    },
    {
      title: t('硬件平台'),
      dataIndex: 'platform',
      key: 'platform',
      width: 110,
      ellipsis: true,
      render: (_: string, record: BSPPackage) =>
        formatVehicle(record.platforms, record.platform, lang),
    },
    {
      title: t('上传状态'),
      dataIndex: 'upload_status',
      key: 'upload_status',
      width: 90,
      render: (status: string, record: BSPPackage) => {
        const map: Record<string, { status: 'success' | 'processing' | 'error' | 'default'; text: string }> = {
          done: { status: 'success', text: t('完成') },
          uploading: { status: 'processing', text: t('上传中') },
          failed: { status: 'error', text: t('失败') },
          pending: { status: 'default', text: t('等待') },
        };
        const cfg = map[status] ?? { status: 'default', text: status || t('完成') };
        const badge = <Badge status={cfg.status} text={cfg.text} />;

        if (status !== 'uploading') return badge;

        const files = record.files ?? [];
        if (!files.length) return badge;

        const docEntries: { key: string; label: string }[] = [
          { key: `package-${record.id}-doc-release_note`, label: 'Release Note' },
          { key: `package-${record.id}-doc-test_report`, label: t('测试报告') },
        ];

        const content = (
          <div style={{ minWidth: 280, maxWidth: 360 }}>
            {files.map((file) => {
              const key = `package-${record.id}-file-${file.id}`;
              const entry = progress[key];
              const pct = resolveUploadPercent(file, entry);
              const fileStatus = entry?.status ?? file.upload_status ?? 'uploading';
              const progressStatus =
                fileStatus === 'failed' ? 'exception' :
                fileStatus === 'done'   ? 'success'   : 'active';
              return (
                <div key={file.id} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#666' }}>{file.file_type?.name}</span>
                    <span style={{ color: '#999' }}>{formatFileSize(file.file_size)}</span>
                  </div>
                  <div style={{ fontSize: 12, marginBottom: 2, wordBreak: 'break-all' }}>{file.file_name}</div>
                  <Progress percent={pct} size="small" status={progressStatus} />
                </div>
              );
            })}
            {docEntries.map(({ key, label }) => {
              const entry = progress[key];
              // store 有记录时优先用 store；否则从 record 字段推导（刷新后 store 为空的场景）
              const isReleaseNote = label === 'Release Note';
              const nameField = isReleaseNote ? record.release_note_name : record.test_report_name;
              const urlField = isReleaseNote ? record.release_note_url : record.test_report_url;
              const derivedStatus = nameField && !urlField ? 'uploading' : (urlField ? 'done' : null);
              const effectiveStatus = entry?.status ?? derivedStatus;
              if (!effectiveStatus || effectiveStatus === 'done') return null;
              const progressStatus = effectiveStatus === 'failed' ? 'exception' : 'active';
              const pct = entry?.percent ?? 0;
              return (
                <div key={key} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>{label}</div>
                  {nameField && <div style={{ fontSize: 12, marginBottom: 2, wordBreak: 'break-all' }}>{nameField}</div>}
                  <Progress percent={pct} size="small" status={progressStatus} />
                  {entry?.currentSpeed !== undefined && entry.currentSpeed > 0 && (
                    <span style={{ fontSize: 11, color: '#999' }}>{formatSpeed(entry.currentSpeed)}</span>
                  )}
                </div>
              );
            })}
          </div>
        );

        return (
          <Popover content={content} title={t('文件上传进度')} trigger="hover" placement="left">
            {badge}
          </Popover>
        );
      },
    },
    {
      title: t('关联文件'),
      key: 'external_releases',
      render: (_: unknown, record: BSPPackage) => {
        const ers = record.external_releases;
        if (!ers?.length) return <span className="text-gray-400">-</span>;
        return (
          <Space size={[4, 4]} wrap>
            {ers.map((r) => (
              <Tooltip key={r.id} title={r.name}>
                <Tag color="purple" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 0 }}>{r.name}</Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: t('文件数量'),
      dataIndex: 'files_count',
      key: 'files_count',
      width: 40,
      render: (name: string) => <Tag>{name}</Tag>,
    },
    {
      title: t('文件大小'),
      dataIndex: 'total_size',
      key: 'total_size',
      width: 80,
      render: (size: number) => formatFileSize(size || 0),
    },
    {
      title: t('上传者'),
      dataIndex: ['created_by', 'username'],
      key: 'created_by',
      width: 80,
    },
    {
      title: t('上传时间'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 87,
      render: (date: string) => formatDate(date),
    },
    {
      title: t('操作'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title={t('查看详情')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/package/${record.id}${projectId ? `?projectId=${projectId}` : ''}`)}
            />
          </Tooltip>

          {onEdit && (
            <Tooltip title={t('编辑')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
          )}

          {isInternal && (
            <Tooltip title={t('制品包')}>
              <Button
                type="text"
                size="small"
                icon={<InboxOutlined />}
                onClick={() => navigate(`/packages/${record.project.id}/artifacts/${record.id}`)}
              />
            </Tooltip>
          )}

          {isInternal && projectId && (
            <BcrButton
              projectId={projectId}
              packageId={record.id}
              status={bcrStatusMap[String(record.id)]?.status ?? null}
            />
          )}

          {onViewLogs && (
            <Tooltip title={t('操作日志')}>
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => onViewLogs(record)}
              />
            </Tooltip>
          )}

          {isAdmin && (
            <Tooltip title={t('删除')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => confirmDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table<BSPPackage>
      columns={columns}
      dataSource={packages}
      loading={loading}
      rowKey="id"
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => t('共 {total} 条', { total }),
        onChange: onPageChange,
      }}
      scroll={{}}
      size="middle"
    />
  );
};

export default PackageList;
